/*
 * hercase.vercel.app · a water ring-toss portfolio by Hernan Case · source: github.com/hercase/hercase-playground
 * MIT, with attribution: a site built from this one should say "Inspired by Hernan Case, hercase.vercel.app"
 * where visitors can see it. Say hi: hercase92@gmail.com
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiLinkedin, FiGithub, FiMail, FiUser } from 'react-icons/fi'
import {
  AboutPanel,
  ClickHint,
  ClickSpark,
  FpsMeter,
  ActionRow,
  RotatingText,
  ScorePill,
  SafeBallpit,
  TankGlass,
  TitleRow,
} from './components'
import { hasWebGL } from './lib/webgl'
import { tierKnobs, wantsLightPage } from './lib/tier'
import { trackEvent } from './lib/analytics'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import type { LetterBox } from './components/Ballpit/letterPegs'
import { titleBoxesPx } from './components/Ballpit/titleLayout'
import type { LetterTarget } from './components/TitleRow'
import { TitleOutline } from './components/TitleOutline'
import { ringCountFor, ringSizesFor } from './components/Ballpit/ringScale'
import type { ScoreMark } from './components/Ballpit'
import { POP_FLIGHT_MS, ScorePops, type ScorePop } from './components/ScorePops'

/** Several catches at once fly in one after another, this far apart. */
const POP_STAGGER_MS = 90

/** The links out, in the brand's colours; About, the page's one primary action, joins them in the row. */
const links = [
  { icon: <FiLinkedin size={18} />, color: '#ff2bff', label: 'LinkedIn', link: 'https://www.linkedin.com/in/hercase/' },
  { icon: <FiGithub size={18} />, color: '#2bf5ff', label: 'GitHub', link: 'https://github.com/hercase' },
  { icon: <FiMail size={18} />, color: '#ffef3a', label: 'Email', link: 'mailto:hercase92@gmail.com' },
]

/** How long the 3D scene may take to appear before the page gives up on it: a slow network has 3 MB of physics to fetch, a slow GPU shaders to compile. */
const STALL_MS = 25000

const sameTargets = (a: LetterTarget[] | null, b: LetterTarget[] | null) =>
  a === b ||
  (!!a &&
    !!b &&
    a.length === b.length &&
    a.every(
      (box, i) =>
        Math.abs(box.left - b[i].left) < 0.25 &&
        Math.abs(box.top - b[i].top) < 0.25 &&
        Math.abs(box.width - b[i].width) < 0.25 &&
        Math.abs(box.height - b[i].height) < 0.25
    ))

function App() {
  const [aboutOpen, setAboutOpen] = useState(false)
  /** The visitor has opened the profile at least once this visit. */
  const [aboutSeen, setAboutSeen] = useState(false)
  useEffect(() => {
    if (aboutOpen) setAboutSeen(true)
  }, [aboutOpen])
  const buttonRef = useRef<HTMLButtonElement>(null)

  const closeAbout = useCallback(() => {
    setAboutOpen(false)
    buttonRef.current?.focus()
  }, [])

  const reduceMotion = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, [])
  // The 3D scene draws the title itself. Until it is ready the DOM title stands in, dimmed;
  // it stays for good when the scene cannot run or fails. A preference for reduced motion does
  // not switch the scene off: it runs calm, moving only when pressed.
  /** Why the scene is not running, when it is not: a word for the console, and a note for the visitor where it helps. */
  const [sceneOff] = useState(() => {
    const reason = wantsLightPage() ? 'data saver or 2G' : !hasWebGL() ? 'no WebGL2' : null
    if (reason) {
      // Said once, so a visitor who sees the plain title can tell us why.
      console.info(`[hercase] 3D scene off: ${reason}`)
      document.documentElement.dataset.scene = `off:${reason}`
    }
    return reason
  })
  const [sceneExpected, setSceneExpected] = useState(() => !sceneOff)
  const onFail = useCallback(() => setSceneExpected(false), [])
  type Controls = { setPaused: (paused: boolean) => void }
  const controlsRef = useRef<Controls | null>(null)
  const onControls = useCallback((controls: Controls) => {
    controlsRef.current = controls
  }, [])
  // The water stands still behind the About dialog: nothing to see there, and the blur over a live canvas is costly.
  useEffect(() => {
    controlsRef.current?.setPaused(aboutOpen)
  }, [aboutOpen])
  // Ring size follows the viewport: shrink on tall monitors (world units scale with height) and on
  // narrow screens, where the title is small and a full-size ring would dwarf the letters.
  const ringSizes = useMemo(() => ringSizesFor(window.innerWidth, window.innerHeight), [])
  // More rings on wide screens so the pile stays full. Count is capped: collisions are O(n²).
  const hasPointer = useMemo(() => window.matchMedia('(hover: hover) and (pointer: fine)').matches, [])
  const ringCount = useMemo(
    () => Math.min(ringCountFor(window.innerWidth, window.innerHeight, !hasPointer), tierKnobs().ringCap),
    [hasPointer]
  )

  // The title reports its letters every time they move; the ring pit reads them each frame.
  const lettersRef = useRef<LetterBox[]>([])
  // The loading title is set by the 3D row's own rule, so it stands exactly where the letters will.
  const [titleTargets, setTitleTargets] = useState<LetterTarget[] | null>(null)
  const onMeasure = useCallback((letters: LetterBox[]) => {
    lettersRef.current = letters
    const next = titleBoxesPx(letters, window.innerWidth, window.innerHeight)
    setTitleTargets((prev) => (sameTargets(prev, next) ? prev : next))
  }, [])
  const getLetters = useCallback(() => lettersRef.current, [])
  const [tossed, setTossed] = useState(false)
  const tossedRef = useRef(false)
  // The visitor's best, kept between visits.
  const [best, setBest] = useState(() => {
    try {
      return Number(localStorage.getItem('hc-best-3') ?? 0) || 0
    } catch {
      return 0
    }
  })
  const bestRef = useRef(best)
  // What the scoreboard shows: the score as it stands, but a gain only once its +1 has flown in.
  const [shown, setShown] = useState({ score: 0, best })
  const latestRef = useRef(shown)
  const [pops, setPops] = useState<ScorePop[]>([])
  const nextPopId = useRef(0)
  const onPopDone = useCallback((id: number) => setPops((all) => all.filter((pop) => pop.id !== id)), [])
  const onScore = useCallback(
    (hooked: number, gains: ScoreMark[], losses: ScoreMark[]) => {
      // A best is won by playing: rings that land on the letters as the scene rains in count
      // for the score, not for the record.
      if (tossedRef.current && hooked > bestRef.current) {
        bestRef.current = hooked
        try {
          localStorage.setItem('hc-best-3', String(hooked))
        } catch {
          // Storage blocked: the best lives for this visit only.
        }
        if (hooked >= 2) trackEvent('new_best', { best: hooked })
        setBest(hooked)
      }
      latestRef.current = { score: hooked, best: bestRef.current }
      // Always the latest reading: a pop landing late never puts an older number back.
      const show = () => setShown(latestRef.current)
      if (!tossedRef.current || reduceMotion) {
        show()
        return
      }
      const added: ScorePop[] = gains.map((gain, i) => ({
        ...gain,
        kind: 'gain',
        label: `+${gain.points}`,
        id: nextPopId.current++,
        delay: i * POP_STAGGER_MS,
      }))
      // Fallen rings: a −1 where each one hung, sinking away as the number drops.
      for (const [i, loss] of losses.entries()) {
        added.push({
          ...loss,
          kind: 'loss',
          label: `−${loss.points}`,
          id: nextPopId.current++,
          delay: i * POP_STAGGER_MS,
        })
      }
      setPops((all) => [...all, ...added])
      if (gains.length) window.setTimeout(show, POP_FLIGHT_MS * 0.97 + (gains.length - 1) * POP_STAGGER_MS)
      else show()
    },
    [reduceMotion]
  )
  // The star ring: caught once, kept for good, like the best.
  const [star, setStar] = useState(() => {
    try {
      return localStorage.getItem('hc-star') === '1'
    } catch {
      return false
    }
  })
  /** Bumped by every catch of the star ring, so the scoreboard celebrates each one. */
  const [starCatches, setStarCatches] = useState(0)
  const onStar = useCallback(
    (at: ScoreMark) => {
      // Like the best, it is won by playing, not by the rings raining in.
      if (!tossedRef.current) return
      setStar((had) => {
        trackEvent('star_ring', { first: !had })
        return true
      })
      try {
        localStorage.setItem('hc-star', '1')
      } catch {
        // Storage blocked: the star lives for this visit only.
      }
      setStarCatches((n) => n + 1)
      if (!reduceMotion) {
        setPops((all) => [...all, { ...at, kind: 'star', label: '★', delay: 0, id: nextPopId.current++ }])
      }
    },
    [reduceMotion]
  )
  const onToss = useCallback(() => {
    tossedRef.current = true
    // Counted once per visit: did the visitor play at all?
    setTossed((was) => {
      if (!was) trackEvent('toss')
      return true
    })
  }, [])
  const [sceneReady, setSceneReady] = useState(false)
  // Ready wins over an earlier stall or failure: a tab opened in the background reports late.
  const onReady = useCallback(() => {
    setSceneReady(true)
    setSceneExpected(true)
  }, [])
  // A scene that never reports ready (a stalled download, a GPU that gives up) is treated as
  // failed after a while: the DOM title comes back in full rather than staying dimmed.
  useEffect(() => {
    if (!sceneExpected || sceneReady) return
    const id = window.setTimeout(() => setSceneExpected(false), STALL_MS)
    return () => window.clearTimeout(id)
  }, [sceneExpected, sceneReady])

  return (
    <ClickSpark sparkColor="#fff" sparkSize={10} sparkRadius={15} sparkCount={8} duration={400}>
      {/* A toy, not a document: dragging the pointer about must not select the labels around it. */}
      <main className="bg-black p-4 w-full h-dvh flex items-center justify-center overflow-hidden select-none">
        {/* The column sits at the centre: room above the title for tossed rings to clear it. */}
        <div className="relative w-[min(88vw,140vh)] md:w-[min(80vw,140vh)] lg:w-[min(70vw,140vh)] short:w-[min(88vw,95vh)] max-w-[1440px] mx-auto flex flex-col items-center z-10">
          {/* The DOM title lays the letters out and carries the heading for assistive tech and
              crawlers; the 3D letters stand exactly in its place, so it is hidden whenever the
              scene can run. */}
          <div className="relative z-30 w-full pb-[3vh]">
            <div
              className={`transition-opacity duration-700 ease-out motion-reduce:transition-none ${
                !sceneExpected ? '' : sceneReady ? 'opacity-0' : 'opacity-40'
              }`}
            >
              <div>
                {/* While the scene loads, a light sweeps across the title: loading, not switched off. */}
                {titleTargets && !sceneReady && (
                  <TitleOutline boxes={titleTargets} className={sceneExpected ? 'title-shimmer' : ''} />
                )}
                <TitleRow
                  text="Hernan Case"
                  fontFamily="Title"
                  textColor={titleTargets ? 'transparent' : '#FFFFFF'}
                  minFontSize={36}
                  onMeasure={onMeasure}
                  letterBoxes={sceneExpected ? titleTargets : null}
                />
              </div>
            </div>
            <RotatingText
              texts={[
                'Tech Lead · Principal Software Engineer',
                'Frontend architecture for global brands',
                'Teams that ship, standards that last',
                'From discovery to launch',
                'AI in the loop, not in the slogan',
              ]}
              className="mt-6 short:mt-2 text-center select-none"
            />
          </div>
          <div className="relative z-10 mt-8 flex flex-col items-center short:mt-4">
            <ActionRow
              primaryRef={buttonRef}
              onItemClick={(label) => trackEvent('link_click', { link: label, from: 'hero' })}
              items={[
                ...links,
                {
                  icon: <FiUser size={18} />,
                  color: '#ffffff',
                  label: 'About',
                  primary: true,
                  // Once the visitor has played and not yet looked, the chip asks for a look.
                  attention: tossed && !aboutSeen,
                  onClick: () => {
                    setAboutOpen(true)
                    trackEvent('about_open')
                  },
                  'aria-haspopup': 'dialog',
                  'aria-expanded': aboutOpen,
                },
              ]}
            />
            {sceneExpected && <ClickHint ready={sceneReady} tossed={tossed} touch={!hasPointer} best={best} />}
          </div>
        </div>

        {sceneExpected && (
          <>
            {/* The scene and everything that only makes sense with it. */}
            <SafeBallpit
              count={ringCount}
              minSize={ringSizes.minSize}
              maxSize={ringSizes.maxSize}
              size0={ringSizes.size0}
              getLetters={getLetters}
              calm={reduceMotion}
              onToss={onToss}
              onScore={onScore}
              onReady={onReady}
              onFail={onFail}
              onControls={onControls}
              onStar={onStar}
            />
            {/* A faint pool of light behind the title and film grain over the whole scene: the
                black stops being flat without a single asset. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_55%_40%_at_50%_38%,rgba(255,255,255,0.045),transparent_70%)]"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-[1] opacity-[0.045] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%22160%22 height=%22160%22 filter=%22url(%23n)%22/></svg>')] bg-[length:160px_160px]"
            />
            {/* The tank's glass, on the physics' own lines. */}
            <TankGlass />
            {/* The pile fades into black towards the bottom edge, so the title keeps the eye. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[32vh] bg-gradient-to-t from-black/60 via-black/20 to-transparent"
            />
            {tossed && <ScorePill score={shown.score} best={shown.best} star={star} starCatches={starCatches} />}
            <ScorePops pops={pops} onDone={onPopDone} />
          </>
        )}

        {/* No WebGL at all is nearly always graphics acceleration switched off in the browser: say so, since the visitor can fix it. */}
        {sceneOff === 'no WebGL2' && (
          <p className="pointer-events-none fixed inset-x-0 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-[25] px-6 text-center text-[11px] uppercase tracking-[0.2em] text-white/55 md:text-xs">
            There is a water ring-toss here, but your browser has graphics acceleration off. Turn it on in the browser's
            system settings and reload to play.
          </p>
        )}

        <AboutPanel open={aboutOpen} onClose={closeAbout} />

        {/* Frame rate read-out while developing; never in a production build. */}
        {import.meta.env.DEV && <FpsMeter />}
        {/* Vercel's page views, custom events and Core Web Vitals. */}
        <Analytics debug={false} />
        <SpeedInsights />

        {/* Same content as the About panel, always in the DOM for screen readers and crawlers. */}
        <section className="sr-only" aria-label="About Hernan Case">
          <h2>About Hernan Case</h2>
          <p>
            Agency work for global brands in food service, streaming, athleisure, energy, pet care and luxury retail,
            under NDA: client names are on LinkedIn and the code stays private. This site's source is public on GitHub.
          </p>
          <p>Principal Software Engineer and Tech Lead at DEPT® since 2022, Principal since 2025.</p>
          <p>Remote, from Argentina (UTC−3). Open to principal or staff frontend and tech-lead roles.</p>
          <p>
            Fifteen years on the web, from founding an online store at 17 to owning frontend architecture for global
            brands in food service, streaming, e-commerce and luxury retail.
          </p>
          <ul>
            <li>
              Technical leadership: architecture, standards and tooling across teams, mentoring, cross-functional work.
            </li>
            <li>React, Next.js, TypeScript, SSR and SSG, GraphQL, design systems, accessibility, Core Web Vitals.</li>
            <li>Headless CMS: Sanity, Contentful, Contentstack. AI-assisted engineering every day.</li>
          </ul>
          <p>
            <a href="/llms.txt">Full profile as plain text</a>
          </p>
        </section>
      </main>
    </ClickSpark>
  )
}

export default App
